import React, { createContext, useContext, useEffect, useState } from 'react';
import { UserManager } from 'oidc-client-ts';
import oidcConfig from '../config/oidc';
import { showAuthError, ERROR_CODES } from '../assets/js/authErrors';

const AuthContext = createContext();

// Instancia singleton — compartida con useLogout y OidcCallback
export const userManager = new UserManager(oidcConfig);

// ── Dev bypass ────────────────────────────────────────────────────────────────
// Activo solo cuando se compila con REACT_APP_DEV_BYPASS=true.
// Nunca presente en .env.production → nunca llega a producción real.
const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS === 'true';

const MOCK_OIDC_USER = {
  profile: {
    sub: 'fe0a5715-a910-4974-a7a0-0a59c8586b29',
    email: 'viri@cliente.com',
    given_name: 'Viri',
    family_name: 'Perez',
    name: 'Viri Perez',
    preferred_username: 'viri',
  },
  access_token: 'dev-bypass-token',
  expired: false,
};

const MOCK_DB_USER = {
  id: 'fe0a5715-a910-4974-a7a0-0a59c8586b29',
  correo: 'viri@cliente.com',
  nombre: 'Viri',
  apellido: 'Perez',
  perfil: 'Admin',
  activo: true,
  fechaCreacion: 1775623838373,
  ultimaConexion: 1776868359430,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:0542';
const SESSION_KEY = process.env.REACT_APP_VARIABLE_SESION_STORAGE;

const decodeJwtInfo = (accessToken) => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return {
      aud: payload.aud,
      iss: payload.iss,
      exp: new Date(payload.exp * 1000).toLocaleString('es-MX'),
    };
  } catch {
    return null;
  }
};

const apiFetch = async (method, path, accessToken, body = null) => {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
  };
  if (body) opts.body = JSON.stringify(body);

  let res;
  try {
    res = await fetch(`${API_URL}/api${path}`, opts);
  } catch (e) {
    const err = new Error('Sin conexión con el servidor');
    err.code = ERROR_CODES.API_NETWORK;
    throw err;
  }

  if (!res.ok) {
    const codeMap = { 401: ERROR_CODES.API_UNAUTHORIZED, 403: ERROR_CODES.API_FORBIDDEN };
    const errCode = codeMap[res.status] || ERROR_CODES.API_SERVER;
    const err = new Error(`API ${res.status}`);
    err.code = errCode;
    throw err;
  }
  return res.json();
};

/**
 * Carga el perfil del usuario desde sessionStorage (caché) o desde la VPS API.
 * Si no existe en la BD, lo registra automáticamente con los datos del OIDC.
 */
const cargarPerfil = async (oidcUser) => {
  try {
    // 1. Intentar desde caché para evitar requests en recargas y dobles llamadas
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached) {
      return JSON.parse(cached);
    }

    const { email, sub, given_name, family_name, name, preferred_username } = oidcUser.profile;
    const token = oidcUser.access_token;

    // 2. Buscar usuario por correo en la BD
    let readResult;
    try {
      readResult = await apiFetch(
        'GET',
        `/read?path=usuarios&orderBy=correo&equalTo=${encodeURIComponent(email)}`,
        token
      );
    } catch (e) {
      e.code = e.code || ERROR_CODES.DB_READ_FETCH;
      throw e;
    }

    const { data } = readResult;
    let dbUser = null;
    if (data) {
      const entries = Object.entries(data);
      if (entries.length > 0) {
        const [key, val] = entries[0];
        // `id` es el documento del colaborador; la llave real del nodo va aparte en `firebaseKey`
        dbUser = { id: key, ...val, firebaseKey: key };
      }
    }

    // 3. Auto-registro si es la primera vez
    if (!dbUser) {
      const userId = sub;
      const nuevoUsuario = {
        id: userId,
        correo: email,
        nombre: given_name || name?.split(' ')[0] || preferred_username || email,
        apellido: family_name || name?.split(' ').slice(1).join(' ') || '',
        perfil: 'Usuario',
        activo: true,
        fechaCreacion: Date.now(),
        ultimaConexion: Date.now(),
      };

      try {
        await apiFetch('POST', '/write', token, {
          path: `usuarios/${userId}`,
          data: nuevoUsuario,
          mode: 'set',
        });
      } catch (e) {
        e.code = e.code || ERROR_CODES.DB_WRITE_REGISTER;
        throw e;
      }

      dbUser = { ...nuevoUsuario, firebaseKey: userId };
      console.info('Usuario registrado automáticamente:', email);
    } else {
      // 4. Actualizar última conexión sin bloquear
      apiFetch('POST', '/write', token, {
        path: `usuarios/${dbUser.firebaseKey}/ultimaConexion`,
        data: Date.now(),
        mode: 'set',
      }).catch(() => {});
    }

    sessionStorage.setItem(SESSION_KEY, JSON.stringify(dbUser));
    return dbUser;
  } catch (e) {
    // Re-lanzar para que el llamador muestre la alerta con el código correcto
    throw e;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(DEV_BYPASS ? MOCK_OIDC_USER : null);
  const [dbUser, setDbUser] = useState(DEV_BYPASS ? MOCK_DB_USER : null);
  const [loading, setLoading] = useState(!DEV_BYPASS);

  const loginWithOidc = () => {
    userManager.signinRedirect();
  };

  useEffect(() => {
    // En modo bypass no hay flujo OIDC
    if (DEV_BYPASS) return;

    const logAuth = (oidcUser, action, metadata = {}) => {
      fetch(`${API_URL}/api/auth-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${oidcUser.access_token}`,
        },
        body: JSON.stringify({ action, metadata }),
      }).catch(() => {});
    };

    const handleUserLoaded = async (oidcUser) => {
      try {
        const perfil = await cargarPerfil(oidcUser);
        setUser(oidcUser);
        setDbUser(perfil);
        logAuth(oidcUser, 'login_success');
      } catch (e) {
        const jwtInfo = oidcUser?.access_token ? decodeJwtInfo(oidcUser.access_token) : null;
        showAuthError(e.code || ERROR_CODES.DB_READ_FETCH, e.message, jwtInfo);
        setUser(oidcUser);
        setDbUser(null);
        logAuth(oidcUser, 'login_error', { errorCode: e.code, jwtInfo });
      } finally {
        setLoading(false);
      }
    };

    const handleUserUnloaded = () => {
      sessionStorage.clear();
      setUser(null);
      setDbUser(null);
      setLoading(false);
    };

    const handleSilentRenewError = (err) => {
      sessionStorage.clear();
      setUser(null);
      setDbUser(null);

      const isNetworkError =
        err?.message?.includes('Failed to fetch') ||
        err?.message?.includes('ERR_INTERNET_DISCONNECTED') ||
        err?.message?.includes('NetworkError');

      if (isNetworkError) {
        console.warn('[AUTH-002] Sin conexión al renovar sesión, sesión cerrada silenciosamente');
        return;
      }

      showAuthError(ERROR_CODES.AUTH_SILENT_RENEW, err?.message);
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    userManager.getUser().then(async (oidcUser) => {
      if (oidcUser && !oidcUser.expired) {
        try {
          const perfil = await cargarPerfil(oidcUser);
          setUser(oidcUser);
          setDbUser(perfil);
        } catch (e) {
          const jwtInfo = oidcUser?.access_token ? decodeJwtInfo(oidcUser.access_token) : null;
          showAuthError(e.code || ERROR_CODES.DB_READ_FETCH, e.message, jwtInfo);
          setUser(oidcUser);
          setDbUser(null);
          logAuth(oidcUser, 'login_error', { errorCode: e.code, jwtInfo });
        }
      }
      setLoading(false);
    }).catch((e) => {
      showAuthError(ERROR_CODES.AUTH_SESSION_CHECK, e?.message);
      setLoading(false);
    });

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  const value = { user, dbUser, loading, loginWithOidc };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
